import React, { useState, useEffect } from 'react';
import apiClient from '../../api/client';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';

// --- Komponen Card untuk setiap pemimpin ---
const LeaderCard = ({ leader }) => {
    return (
        <div className="mb-12 flex items-start -ml-11 group">
            {/* Titik Timeline */}
            <div className="w-8 h-8 flex-shrink-0 bg-blue-600 rounded-full border-4 border-white z-10 -ml-1 transition-colors duration-300 group-hover:bg-blue-700 shadow-md"></div>

            {/* Konten Timeline Card */}
            <div className="relative flex-grow pl-8">
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden p-5 sm:p-6 text-gray-800 transition-all duration-500 transform group-hover:scale-[1.01] group-hover:shadow-xl group-hover:border-blue-500">

                    {/* Menggunakan flex-row-reverse untuk gambar di KANAN */}
                    <div className="flex flex-col sm:flex-row-reverse items-start sm:items-center space-y-4 sm:space-y-0 sm:gap-6">

                        {/* Foto (Kanan) - Kotak dengan Sudut Membulat */}
                        <div className="flex-shrink-0">
                            <img
                                src={leader.image}
                                alt={leader.nama_lengkap}
                                // Gambar Kotak (rounded-xl)
                                className="w-24 h-26 sm:w-28 sm:h-30 rounded-xl object-cover border-2 border-blue-500 shadow-lg transition duration-300"
                            />
                        </div>

                        {/* Nama, Periode, dan Jabatan (Kiri) */}
                        <div className="text-left flex-grow sm:order-first">
                            {/* Jabatan sebagai label utama */}
                            <p className="text-sm font-semibold text-blue-500 mb-1 uppercase tracking-wider">
                                {leader.jabatan || 'Ketua Majelis Jemaat'}
                            </p>
                            {/* Nama sebagai judul utama */}
                            <h3 className="text-2xl sm:text-3xl font-extrabold leading-snug text-gray-900 group-hover:text-blue-700 transition-colors">
                                {leader.nama_lengkap}
                            </h3>
                            {/* Periode sebagai badge/info */}
                            <p className="text-md font-semibold text-gray-600 mt-2 bg-blue-50 p-1 px-3 rounded-full inline-block border border-blue-200">
                                <span className="mr-2 text-sm">&#128337;</span> {/* Ikon Jam */}
                                {leader.periode}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
// --- Akhir Komponen Leader Card ---

const KepemimpinanKmj = () => {
    const [kmjList, setKmjList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        let isCancelled = false;
        let progressInterval;
        const MIN_LOADING_TIME = 1000;
        const DELAY_AFTER_LOAD = 700;

        const fetchKmjData = async () => {
            setIsLoading(true);
            setError(null);
            setProgress(0);

            const startTime = Date.now();
            progressInterval = setInterval(() => {
                const elapsedTime = Date.now() - startTime;
                let calculatedProgress = (elapsedTime / MIN_LOADING_TIME) * 100;
                if (calculatedProgress > 99) calculatedProgress = 99;
                if (!isCancelled) {
                    setProgress(Math.floor(calculatedProgress));
                }
            }, 50);

            try {
                const [kmjData] = await Promise.all([
                    apiClient.getKmjList(),
                    new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME)),
                ]);

                if (!isCancelled) {
                    // Sorting opsional dihilangkan agar tampil sesuai urutan API atau yang diinginkan user
                    setKmjList(kmjData ?? []);
                }
            } catch (err) {
                if (!isCancelled) {
                    const errorMessage = err.message || 'Gagal mengambil data kepemimpinan gereja. Coba ulangi atau periksa koneksi server.';
                    setError(errorMessage);
                    console.error('API Fetch Error:', err);
                }
            } finally {
                clearInterval(progressInterval);
                if (!isCancelled) {
                    setProgress(100);
                    setTimeout(() => {
                        setIsLoading(false);
                    }, DELAY_AFTER_LOAD);
                }
            }
        };

        fetchKmjData();

        return () => {
            isCancelled = true;
            clearInterval(progressInterval);
        };
    }, []);

    const handleRetry = () => {
        window.location.reload();
    };

    // Error handling terpusat di sini
    if (error && !isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-100">
                <ErrorDisplay message={error} onRetry={handleRetry} className="max-w-md mx-auto" />
            </div>
        );
    }


    return (
        <div className="bg-gray-100 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">

                {/* Header Section yang diperkuat */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 relative overflow-hidden border-b-8 border-blue-700 mb-8">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 text-center mb-2">
                        SEJARAH KEPEMIMPINAN GEREJA
                    </h1>
                    <p className="text-lg text-gray-500 text-center font-medium">
                        GPIB Jemaat "Kasih Karunia" Badung-Bali
                    </p>
                </div>

                {/* Kontainer Loading dan Data */}
                <div className="mt-8 bg-white rounded-xl shadow-lg p-6 sm:p-10 min-h-[500px] relative">
                    {isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingSpinner progress={progress} />
                        </div>
                    ) : (
                        <PageTransitionWrapper>
                            {kmjList.length === 0 ? (
                                <div className="text-center py-16 text-gray-500 text-lg font-medium">
                                    <span className="text-5xl block mb-4 text-red-500">⚠️</span>
                                    <p className="text-xl font-semibold text-gray-700">Data kepemimpinan belum tersedia.</p>
                                    <p className="text-sm text-gray-500 mt-1">Silakan cek kembali di lain waktu.</p>
                                </div>
                            ) : (
                                <div className="relative border-l-4 border-blue-300 pl-8 pt-4">
                                    {/* Timeline Utama */}
                                    {kmjList.map((leader) => (
                                        <LeaderCard key={leader.id} leader={leader} />
                                    ))}
                                </div>
                            )}
                        </PageTransitionWrapper>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KepemimpinanKmj;